/**
 * Netxol AI work that takes minutes on a CPU (Workflow AI, Dashboard AI…) runs as a background task of the console: the
 * request keeps going while the user moves to other screens (the page is never reloaded), a small indicator in the top
 * bar shows what is running, and a notification with "Open" appears when it is ready. Closing the browser tab does not
 * lose the work either — the server finishes and saves the draft in its list.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';

interface Task { id: number; label: string; started: number }
const useTasks = create<{ tasks: Task[] }>(() => ({ tasks: [] }));
let nextId = 1;
let go: ((to: string) => void) | null = null;

/** A Netxol AI builder as a server job: start it, then ask for its state every few seconds until it is done. */
export async function aiServerJob<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const { core } = await import('@/shared/api/client');
  const start = (await core.post(path, { ...body, background: true })).data as { jobId: string };
  for (;;) {
    await new Promise((r) => setTimeout(r, 3000));
    const j = (await core.get(`/ai/jobs/${start.jobId}`)).data as { status: string; result: T; error: string | null };
    if (j.status === 'done') return j.result;
    if (j.status === 'failed') throw new Error(j.error ?? t('bg.failed'));
  }
}

/** Run `work` in the background; when it resolves, `done(result)` gives the toast text and the page to open. */
export function runInBackground<T>(label: string, work: () => Promise<T>, done: (r: T) => { message: string; open?: string }, fail: (e: unknown) => string) {
  const task = { id: nextId++, label, started: Date.now() };
  useTasks.setState((s) => ({ tasks: [...s.tasks, task] }));
  toast.info(t('bg.started', { label }), { description: t('bg.leave_ok') });
  void work().then((r) => {
    const d = done(r);
    toast.success(d.message, { duration: 30_000, ...(d.open ? { action: { label: t('bg.open'), onClick: () => go?.(d.open!) } } : {}) });
  }, (e) => { toast.error(fail(e)); }).finally(() => useTasks.setState((s) => ({ tasks: s.tasks.filter((x) => x.id !== task.id) })));
}

/** Top-bar indicator of running background tasks (also lends the router's navigate to the "Open" buttons). */
export function BackgroundTasks() {
  const nav = useNavigate(); const tasks = useTasks((s) => s.tasks);
  useEffect(() => { go = nav; return () => { go = null; }; }, [nav]);
  useEffect(() => {   // closing the tab while work runs: the browser asks first (the server still saves the draft)
    if (!tasks.length) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [tasks.length]);
  if (!tasks.length) return null;
  return (
    <span className="hidden items-center gap-1.5 rounded-full border border-violet-300/60 bg-violet-500/10 px-2.5 py-1 text-[11.5px] font-medium text-violet-700 md:flex dark:text-violet-300"
      title={tasks.map((x) => x.label).join('\n')} data-testid="bg-tasks">
      <Loader2 className="size-3.5 animate-spin" /><Sparkles className="size-3.5" />{tasks.length === 1 ? tasks[0].label : t('bg.n_running', { n: tasks.length })}
    </span>
  );
}
